import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { platforms } from '@/mocks/platforms';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">该文章不存在或已被移除</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedPlatforms = platforms.filter(p => article.platforms.includes(p.id));
  const relatedArticles = articles.filter(a => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '樱花视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hongfengyi.cn/article/${article.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '哪个视频平台最适合在线观看电影？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '香蕉视频和樱花视频都是观看电影的不错选择。香蕉视频以最新电影资源见长，樱花视频则拥有更全面的影视资源库。具体选择哪个平台，取决于您对内容更新速度和资源丰富度的侧重。',
        },
      },
      {
        '@type': 'Question',
        name: '在线视频平台播放卡顿怎么办？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '播放卡顿通常与网络环境有关。建议：1) 连接稳定的WiFi网络；2) 降低视频画质；3) 选择支持自适应码率的平台如蜜桃视频；4) 关闭其他占用带宽的应用；5) 尝试使用平台的预加载功能。',
        },
      },
      {
        '@type': 'Question',
        name: '免费视频平台安全吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '选择知名度高、用户量大的平台如樱花视频、红桃视频等相对安全。这些平台采用了SSL加密传输等安全措施。建议避免在来路不明的平台输入个人信息，并注意保护个人隐私。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '文章', href: '/category/all' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 text-sm text-foreground-500 mb-4">
                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 whitespace-nowrap">
                  {article.category}
                </span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime} 阅读</span>
                <span>·</span>
                <span>{article.author}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-5 leading-tight">
                {article.title}
              </h1>

              <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </div>

            <div className="rounded-lg overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-auto"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {relatedPlatforms.length > 0 && (
              <div className="mt-10 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">相关平台</h3>
                <div className="flex flex-wrap gap-3">
                  {relatedPlatforms.map((p) => (
                    <a
                      key={p.id}
                      href={p.siteUrl}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer border border-background-200/70"
                    >
                      {p.name}
                      <i className="ri-external-link-line text-xs"></i>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {relatedArticles.length > 0 && (
              <div className="mt-12">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">推荐阅读</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer overflow-hidden"
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}